import {
  parseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import {
  PaymentMethod,
  PaymentSortOption,
  PaymentStatus,
  PaymentType,
} from '@rematter/frontend-lib-api-client';

import { PaymentRecord } from '../types';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

type Args = {
  searchTerm?: string;
  statuses?: PaymentStatus[];
  types?: PaymentType[];
  accountIDs?: string[];
  financialAccountIDs?: string[];
  paymentMethods?: PaymentMethod[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
  limit?: number;
  offset?: number;
  sorting: PaymentSortOption[];
};

export const paymentsQueryAtom = createQueryAtomFactory(
  'finance.payments',
  (params: Args) => ({
    payments: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          status: params.statuses,
          type: params.types,
          paymentType: params.paymentMethods,
          accountIDs: params.accountIDs,
          financialAccountIDs: params.financialAccountIDs,
          dateRange:
            params.dateRangeStart || params.dateRangeEnd
              ? {
                  start: params.dateRangeStart,
                  end: params.dateRangeEnd,
                }
              : undefined,
        },
        pagination: {
          offset: params.offset ?? 0,
          limit: params.limit ?? 10,
        },
        sorting: params.sorting,
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        transactionDate: true,
        paymentType: true,
        paymentMethod: true,
        checkFacilityId: true,
        checkNumber: true,
        checkMemo: true,
        achNumber: true,
        wireNumber: true,
        creatorId: true,
        creatorPlatform: true,
        amount: {
          amount: true,
          currencyCode: true,
        },
        unappliedAmount: {
          amount: true,
          currencyCode: true,
        },
        account: {
          id: true,
          name: true,
        },
        financialAccount: {
          id: true,
          name: true,
          type: true,
        },
        bills: {
          bill: {
            id: true,
            documentNumber: true,
          },
        },
        invoices: {
          invoice: {
            id: true,
            documentNumber: true,
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { payments: PaymentRecord[]; total: number } => {
      const entries = data?.payments.entries;
      if (!entries) {
        return { payments: [], total: 0 };
      }

      return {
        payments: entries?.map((it) => ({
          id: it.id,
          documentNumber: it.documentNumber,
          transactionDate: parseCalendarDate(it.transactionDate),
          amount: toMoney(it.amount),
          unappliedAmount: toMoney(it.unappliedAmount),
          status: it.status,
          paymentType: it.paymentType,
          paymentMethod: it.paymentMethod,
          accountId: it.account?.id ?? null,
          accountName: it.account?.name ?? null,
          financialAccount: it.financialAccount
            ? {
                id: it.financialAccount.id,
                name: it.financialAccount.name,
                type: it.financialAccount.type,
              }
            : null,
          checkFacilityId: it.checkFacilityId ?? null,
          checkNumber: it.checkNumber ?? null,
          checkMemo: it.checkMemo ?? null,
          achNumber: it.achNumber ?? null,
          wireNumber: it.wireNumber ?? null,
          creatorId: it.creatorId ?? null,
          creatorPlatform: it.creatorPlatform ?? null,
          linkedBills: (it.bills ?? []).map((b) => ({
            id: b.bill.id,
            documentNumber: b.bill.documentNumber,
          })),
          linkedInvoices: (it.invoices ?? []).map((inv) => ({
            id: inv.invoice.id,
            documentNumber: inv.invoice.documentNumber,
          })),
          timestamps: {
            createdAt: parseZonedDateTime(it.timestamps.createdAt),
            updatedAt: parseZonedDateTime(it.timestamps.updatedAt),
          },
        })),
        total: data?.payments.page.total ?? 0,
      };
    },
  },
);
